import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  DatePicker,
  message,
  Checkbox,
  Card,
  Alert,
} from "antd";
import IntlMessages from "@iso/components/utility/intlMessages";
import VisitorFormModalWrapper from "./VisitorFormModal.styles";
import {
  formService,
  surveyQuestionService,
  visitorFormService,
  visitorInductionService,
} from "@iso/services";
import _ from "lodash";
import { formTypes } from "@iso/constants/formTypes";
import moment from "moment";
import * as Survey from "survey-react";
import "survey-react/survey.css";
import "survey-react/modern.css";
import SignatureCanvas from "react-signature-canvas";
import GlobalForm from "@iso/containers/Pages/Form/GlobalForm";
import FinalScreen from "@iso/containers/Pages/Form/FinalScreen";
import queryString from "query-string";
import BioForm from "@iso/containers/Pages/AdminPages/FormBuilder/BioForm/BioForm";
import CheckinForm from "@iso/containers/Pages/AdminPages/FormBuilder/CheckinForm/CheckinForm";
import HazardForm from "@iso/containers/Pages/AdminPages/FormBuilder/HazardForm/HazardForm";
import SchemaFormRenderer, { assertPublicUploadSize } from "@iso/components/SchemaFormRenderer";

const { Option } = Select;
Survey.StylesManager.applyTheme("modern");

// The appended confirmation step. Underscore-prefixed like `_checkin`, the
// other step the page adds around the stored schema, so it is recognisable as
// not being part of the form the property authored.
const CONFIRM_KEY = "_induction_confirm";

// Build the question from the induction's own validity, so the wording can
// never promise something different from what is actually accepted.
const confirmStepFor = (embedded) => ({
  id: CONFIRM_KEY,
  title: "Induction",
  formMeta: {
    columns: 1,
    fields: [
      {
        key: CONFIRM_KEY,
        label: embedded && embedded.confirm_months
          ? `Have you completed an induction for this property in the last ${embedded.confirm_months} months?`
          : "Have you completed an induction for this property before?",
        widget: "radio-group",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
});

const VisitorFormModal = (props) => {
  const padRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [dataForm, setDataForm] = useState({});
  // What this check-in owes, resolved from the server before submitting so the
  // page knows whether a step is coming. Null means today's behaviour.
  const [embeddedInduction, setEmbeddedInduction] = useState(null);
  // The induction being shown now, and (required mode only) the check-in answers
  // held back until it is done.
  const [inductionStep, setInductionStep] = useState(null);
  const [heldCheckin, setHeldCheckin] = useState(null);
  const [submittedResult, setSubmittedResult] = useState(null);
  const [inductionBusy, setInductionBusy] = useState(false);
  // "Have you already been inducted?" — asked as the last step of the check-in
  // itself, before anything is submitted. Only added when the automatic lookup
  // found nothing, which on both public paths is routinely because the page had
  // no email or phone when it ran.
  // Set when they said yes and the lookup found nothing — the induction still
  // has to happen, and the screen says why.
  const [inductedNotFound, setInductedNotFound] = useState(false);
  const [visitorForm] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [surveyContent, setSurveyContent] = useState({});
  const [questionList, setQuestionList] = useState([]);
  const [showFinalScreen, setShowFinalScreen] = useState(false);
  const [pdfForm] = Form.useForm();
  // Prefer explicit form token (property landing) over URL query (per-form QR).
  // When formToken prop is passed (even empty), do not fall back to the landing URL token.
  const formToken =
    props.formToken !== undefined && props.formToken !== null
      ? props.formToken
      : (queryString.parse(window.location.search).token || "");

  useEffect(() => {
    setFields([
      {
        name: ["visitor", "token"],
        value: formToken,
      },
    ]);
  }, [formToken]);

  useEffect(() => {
    if (props.propertyId && dataForm.id && formToken) {
      visitorFormService.getSurveyQuestion(formToken).then((res) => {
        if (res && res.code === "0000") {
          let questions = [];
          _.forEach(res.survey_questions, (question, index) => {
            switch (question.question_type) {
              case "yes/no":
                var data = {
                  name: "page_" + question.id,
                  questions: [
                    {
                      type: "radiogroup",
                      choices: ["Yes", "No"],
                      isRequired: true,
                      name: question.id.toString(),
                      title: question.title,
                    },
                  ],
                };
                break;
              case "scale":
                var data = {
                  name: "page_" + question.id,
                  questions: [
                    {
                      type: "radiogroup",
                      choices: [
                        "Strongly Disagree",
                        "Disagree",
                        "Neutral",
                        "Agree",
                        "Strongly Agree",
                      ],
                      isRequired: true,
                      name: question.id.toString(),
                      title: question.title,
                    },
                  ],
                };
                break;
              default:
                var data = {
                  name: "page_" + question.id,
                  questions: [
                    {
                      type: "comment",
                      isRequired: true,
                      name: question.id.toString(),
                      title: question.title,
                    },
                  ],
                };
                break;
            }

            questions.push(data);
          });
          setQuestionList(questions);
        }
      });
    }
  }, [props.propertyId, dataForm.id, formToken]);

  useEffect(() => {
    if (questionList && dataForm) {
      var surveyJSON = {
        title: dataForm.survey_title,
        description: dataForm.survey_description,
        completedHtml:
          "<h3>Thank you for completing the survey!</h3> <h5>Please click Save to submit your survey!</h5>",
        pages: questionList,
      };
      setSurveyContent(surveyJSON);
    }
  }, [questionList, dataForm]);

  const cancelModal = () => {
    props.cancelModal();
    visitorForm.resetFields();
    setLoading(false);
    setShowFinalScreen(false);
  };

  useEffect(() => {
    if (props.formData) {
      setDataForm(props.formData);
      setShowFinalScreen(false);
    }
  }, [props.formData, props.visibleFormModal]);

  const handleSubmit = async (e) => {
    setLoading(true);
    const visitorFormValidation = await visitorForm.validateFields();
    if (!visitorFormValidation.errorFields) {
      pdfForm
        .validateFields()
        .then((values) => {
          setLoading(true);
          onStore(values);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
          setLoading(false);
        });
    }
  };

  const onStore = (values) => {
    const data = {
      form: {
        ...values.form,
        visitor_form_token: formToken,
        type: "pdf_upload",
        signature_path:
          padRef.current &&
          Object.keys(padRef.current).length === 0 &&
          padRef.current.constructor === Object
            ? ""
            : padRef.current.toDataURL("image/png"),
        ...checkinInfoPayload(),
        ...(props.extraFormData || {}),
      },
      visitor: visitorFrom(visitorForm.getFieldsValue().visitor),
    };
    visitorFormService.createVisitorForm(data).then((res) => {
      if (res && res.code === "0000") {
        message.success("Submit success");
        pdfForm.resetFields();
        setLoading(false);
        setShowFinalScreen(true);
        if (props.onSubmitted) {
          props.onSubmitted(res);
        }
      } else {
        message.error((res && res.message) || "Submission failed");
        setLoading(false);
      }
    }).catch((err) => {
      console.error(err);
      message.error("Submission failed. Please check your connection and try again.");
      setLoading(false);
    });
  };

  const submitSurveyData = (survey) => {
    const data = {
      form: {
        visitor_form_token: formToken,
        survey: survey.data,
        type: "self_create",
        signature_path:
          padRef.current &&
          Object.keys(padRef.current).length === 0 &&
          padRef.current.constructor === Object
            ? ""
            : padRef.current.toDataURL("image/png"),
        ...checkinInfoPayload(),
        ...(props.extraFormData || {}),
      },
      visitor: visitorFrom(visitorForm.getFieldsValue().visitor),
    };

    visitorFormService.createVisitorForm(data).then((res) => {
      if (res && res.code === "0000") {
        message.success("Submit success");
        setShowFinalScreen(true);
        if (props.onSubmitted) {
          props.onSubmitted(res);
        }
      } else {
        message.error((res && res.message) || "Submission failed");
      }
    }).catch((err) => {
      console.error(err);
      message.error("Submission failed. Please check your connection and try again.");
    });
  };

  // Schema-engine submit (forms carrying schema_json). Mirrors submitSurveyData
  // but sends the answers as `global_form` (keyed by field key) per the Phase-5
  // contract; the survey path is untouched. Visitor identity is still collected
  // by renderVisitorInfo and sent as `visitor`.
  // Best-effort visitor identity from the schema's own answers (schema forms have
  // no separate Visitor Information block). Falls back to an empty object so the
  // check-in record is still created.
  const buildVisitorFromAnswers = (answers) => {
    const a = answers || {};
    // The renderer nests dotted field keys: a field keyed "name.first" arrives as
    // a.name = { first, last } (antd-form-builder registers the name as
    // key.split(".")). Read both the nested object and the flat-key fallbacks.
    const nameObj = a.name && typeof a.name === "object" ? a.name : {};
    const first = a["name.first"] || nameObj.first || a.firstName || a.first_name || a.first;
    const last = a["name.last"] || nameObj.last || a.lastName || a.last_name || a.last;
    const fullNameAnswer = typeof a.name === "string" ? a.name : undefined;
    const name = a.fullName || a.full_name || fullNameAnswer || [first, last].filter(Boolean).join(" ").trim() || undefined;
    const email = a.email || a.Email || a.emailAddress || undefined;
    const visitor = {};
    if (name) { visitor.name = name; }
    if (email) { visitor.email = email; }
    return visitor;
  };

  // When the caller collected a hard-coded Name/Phone/Email/Purpose screen
  // (property check-in landing), it wins over any values derived from the form.
  const visitorFrom = (fallback) => {
    const info = props.visitorInfo;
    if (info) {
      return { name: info.name, email: info.email, mobile_number: info.phone };
    }
    return fallback;
  };
  // The visitor_info check-in block the backend records (both visitor & contractor).
  const checkinInfoPayload = () => {
    const info = props.visitorInfo;
    if (!info) {
      return {};
    }
    return {
      checkin_info: {
        name: info.name,
        phone: info.phone,
        email: info.email,
        purpose: info.purpose,
        visitor_type: info.visitor_type,
        business_name: info.business_name,
        // Only the property-QR landing sets this — it enables the contractor
        // checkout_token/flow. The per-form QR path leaves it unset.
        enable_checkout: info.enable_checkout,
        // Property-QR landing only: the division a contractor is attending.
        division_id: info.division_id,
        // ...and whether they made contact, asked before this form
        // ("made_contact" | "could_not_contact"), with the tap time (epoch ms).
        division_contact: info.division_contact,
        division_contact_at: info.division_contact_at,
      },
    };
  };

  const handleSchemaSubmit = (answers) => {
    // Schema forms are self-contained: the answers are already validated by
    // SchemaFormRenderer and there's no hardcoded Visitor Information block to
    // validate, so submit directly (no misleading "complete visitor info" toast).
    setLoading(true);

    // Per-form QR path: the leading "Check-in Info" step (prepended in VisitorForm.js)
    // arrives nested under `_checkin`. Lift it into the check-in block and the visitor
    // identity, and drop it from the schema answers so global_form matches the stored
    // schema. The property-QR landing has no `_checkin` (it passes props.visitorInfo).
    let globalForm = answers;
    let checkinBlock;
    let visitorPayload;
    // The appended confirmation step, lifted out the same way `_checkin` is
    // below: it is a routing question, not an answer to this form, and
    // global_form must match the stored schema.
    let declaredInducted = null;
    if (globalForm && Object.prototype.hasOwnProperty.call(globalForm, CONFIRM_KEY)) {
      const { [CONFIRM_KEY]: declared, ...withoutConfirm } = globalForm;
      globalForm = withoutConfirm;
      // A local, NOT state: the branch that acts on it runs later in this same
      // function, where a setState would still read the previous value.
      declaredInducted = declared;
    }
    const stepCi = answers && answers._checkin && typeof answers._checkin === "object" ? answers._checkin : null;
    if (stepCi) {
      const { _checkin, ...rest } = answers;
      globalForm = rest;
      const ci = {
        name: (stepCi.name || "").trim(),
        phone: (stepCi.phone || "").trim(),
        email: (stepCi.email || "").trim(),
        purpose: (stepCi.purpose || "").trim(),
        business_name: (stepCi.business_name || "").trim(),
        // Type comes from the form's PURPOSE (Contractor Check-in / Check-out);
        // the backend is authoritative. No checkout token on this path.
        visitor_type: dataForm && dataForm.purpose === "contractor_check_in" ? "contractor" : "visitor",
      };
      checkinBlock = { checkin_info: ci };
      visitorPayload = { name: ci.name, email: ci.email, mobile_number: ci.phone };
    } else {
      checkinBlock = checkinInfoPayload();
      visitorPayload = visitorFrom(buildVisitorFromAnswers(globalForm));
    }

    const data = {
      form: {
        visitor_form_token: formToken,
        global_form: globalForm,
        type: dataForm.type || "self_create",
        // Optional extras from the caller (e.g. device latitude/longitude + the
        // hard-coded check-in info on the property check-in landing).
        ...checkinBlock,
        ...(props.extraFormData || {}),
      },
      // Prefer the check-in step / caller's collected info; else derive identity
      // from the schema answers (Visitor fields are all optional).
      visitor: visitorPayload,
    };
    // They said on the last step that they have already been inducted. A claim
    // is a reason to LOOK, never a substitute for a record: re-run the same
    // email-or-phone lookup the page ran on open, now that we know who they
    // are. `mode: none` the second time round IS the match.
    //
    // This has to happen before the check-in goes anywhere, because in required
    // mode the whole point is that nothing is recorded until the induction
    // question is settled one way or the other.
    if (declaredInducted === "yes") {
      visitorFormService
        .getEmbeddedInduction(formToken, visitorPayload && visitorPayload.email, visitorPayload && visitorPayload.mobile_number)
        .then((res) => {
          const recognised = res && res.code === "0000" && res.mode === "none";
          if (recognised) {
            // Inducted and on file: this check-in owes nothing more.
            submitCheckin(data, null);
            return;
          }
          // Nothing found. They still do the induction, and are told why —
          // otherwise the form appears and the answer reads as ignored.
          setInductedNotFound(true);
          submitCheckin(data, embeddedInduction);
        })
        .catch(() => {
          // Could not check. Fail toward doing the induction.
          setInductedNotFound(true);
          submitCheckin(data, embeddedInduction);
        });
      return;
    }

    submitCheckin(data, embeddedInduction);
  };

  /**
   * Send the check-in, and deal with whatever induction it still owes.
   *
   * `embedded` is passed rather than read from state so the "already inducted"
   * path above can pass null — the check-in that carries no induction takes the
   * ordinary route, and the server re-checks the claim independently before it
   * accepts a plain submit on a required form.
   */
  const submitCheckin = (data, embedded) => {
    const embeddedInduction = embedded;
    // REQUIRED embedded induction: nothing is recorded until the induction is
    // submitted too, so the check-in answers are held here and both go together.
    // Everything else submits now and deals with the induction afterwards.
    if (embeddedInduction && embeddedInduction.mode === "chain_required") {
      setHeldCheckin(data);
      setInductionStep(embeddedInduction);
      setLoading(false);
      return;
    }

    visitorFormService.createVisitorForm(data).then((res) => {
      if (res && res.code === "0000") {
        const embedded = res.embedded_induction;
        // Optional chaining: the check-in IS recorded — show the induction next
        // rather than the success screen, with a way to skip.
        if (embedded && embedded.mode === "chain_optional" && embedded.token) {
          setInductionStep(embedded);
          setSubmittedResult(res);
          setLoading(false);
          return;
        }
        message.success("Submit success");
        if (embedded && embedded.mode === "send_link" && embedded.sent) {
          message.info(`We've sent your induction to ${embedded.sent_to}`);
        }
        setShowFinalScreen(true);
        if (props.onSubmitted) {
          props.onSubmitted(res);
        }
      } else {
        message.error((res && res.message) || "Submission failed");
      }
      setLoading(false);
    }).catch((err) => {
      console.error(err);
      message.error("Submission failed. Please check your connection and try again.");
      setLoading(false);
    });
  };

  // Ask the server what this check-in owes, before the visitor submits. Asking
  // up front means a person who already holds the induction never sees the step
  // appear and then vanish.
  useEffect(() => {
    // `visibleFormModal` — the prop BOTH callers pass (VisitorForm.js for the
    // per-form QR, PropertyCheckinLanding.js for the landing). This read
    // `visibleVisitorFormModal`, which no caller passes, so the effect never
    // ran: the embed was never fetched, required mode never engaged, and a
    // check-in configured as "not recorded until inducted" was recorded with no
    // induction and no step shown.
    if (!props.visibleFormModal || !formToken) {
      return;
    }
    const info = props.visitorInfo || {};
    visitorFormService
      .getEmbeddedInduction(formToken, info.email, info.phone)
      .then((res) => {
        setEmbeddedInduction(res && res.code === "0000" && res.mode !== "none" ? res : null);
      })
      .catch(() => setEmbeddedInduction(null));
  }, [props.visibleFormModal, formToken, props.visitorInfo]);

  // The check-in form as rendered: the stored schema, plus the confirmation
  // question as a final step when this form asks for one and the automatic
  // lookup came back empty.
  //
  // Appended here rather than shown as a screen after Submit, because the
  // answer decides whether the check-in owes an induction at all — and in
  // required mode nothing may be recorded until that is settled.
  const renderedForm = React.useMemo(() => {
    if (!dataForm || !dataForm.schema_json || !embeddedInduction || !embeddedInduction.confirm) {
      return dataForm;
    }
    const steps = (dataForm.schema_json.steps || []).concat(confirmStepFor(embeddedInduction));
    return { ...dataForm, schema_json: { ...dataForm.schema_json, steps } };
  }, [dataForm, embeddedInduction]);

  // Required mode: the check-in and the induction commit together, or neither
  // does. Anything less would leave the half-record the setting exists to avoid.
  const submitCheckinWithInduction = (inductionAnswers) => {
    setInductionBusy(true);
    visitorFormService
      .createVisitorFormWithInduction({ ...heldCheckin, induction_answers: inductionAnswers })
      .then((res) => {
        if (res && res.code === "0000") {
          message.success("Checked in — induction recorded");
          setInductionStep(null);
          setHeldCheckin(null);
          setShowFinalScreen(true);
          if (props.onSubmitted) {
            props.onSubmitted(res);
          }
        } else {
          message.error((res && res.message) || "Could not record the induction");
        }
      })
      .catch(() => message.error("Could not record the induction. Please try again."))
      .finally(() => setInductionBusy(false));
  };

  // Optional mode: the check-in is already recorded, so the induction goes
  // through the same public endpoint the emailed link uses.
  const submitOptionalInduction = (inductionAnswers) => {
    setInductionBusy(true);
    visitorInductionService
      .submitPublic(inductionStep.token, inductionAnswers)
      .then((res) => {
        if (res && res.success) {
          // "Recorded", not "completed": the visitor's part is done, and the
          // property still has to verify the answers.
          message.success("Induction recorded");
        } else {
          message.error((res && res.message) || "Could not record the induction");
          return;
        }
        finishOptional();
      })
      .catch(() => message.error("Could not record the induction. Please try again."))
      .finally(() => setInductionBusy(false));
  };

  // Leaving the induction is allowed in optional mode — the check-in stands and
  // the induction simply stays Pending.
  const finishOptional = () => {
    setInductionStep(null);
    setShowFinalScreen(true);
    if (props.onSubmitted && submittedResult) {
      props.onSubmitted(submittedResult);
    }
  };

  const handleClear = () => {
    padRef.current.clear();
  };

  const renderVisitorInfo = () => {
    return dataForm.is_checkin_form ? (
      <div className="visitor-information">
        <h3 className="visitor-title">Visitor Information</h3>
        <Form
          form={visitorForm}
          layout="vertical"
          scrollToFirstError
          fields={fields}
          className="form-body"
        >
          <Row>
            <Col xs={{ span: 24 }} md={{ span: 11 }}>
              <Form.Item
                name={["visitor", "name"]}
                className="isoInputWrapper"
                label={<IntlMessages id="formPage.modal.vistor.name" />}
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <Input size="large" placeholder="Input your name" />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 11, offset: 2 }}>
              <Form.Item
                name={["visitor", "email"]}
                label="Email"
                className="isoInputWrapper"
                rules={[
                  {
                    type: "email",
                    message: "The input is not valid Email!",
                  },
                  {
                    required: true,
                    message: "Please input your Email!",
                  },
                ]}
              >
                <Input size="large" placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 24 }} md={{ span: 11 }}>
              <Form.Item
                name={["visitor", "emergency_name"]}
                className="isoInputWrapper"
                label={
                  <IntlMessages id="formPage.modal.vistor.emergencyName" />
                }
              >
                <Input
                  size="large"
                  placeholder="Input your emergency name"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 11, offset: 2 }}>
              <Form.Item
                name={["visitor", "emergency_mobile_number"]}
                className="isoInputWrapper"
                label={
                  <IntlMessages id="formPage.modal.vistor.emergencyContactMobileNumber" />
                }
              >
                <Input
                  size="large"
                  placeholder="Input your emergency mobile number"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    ) : (
      <></>
    );
  };

  return (
    <VisitorFormModalWrapper
      className="user-access-modal"
      forceRender={true}
      maskClosable={false}
      visible={props.visibleFormModal}
      title={
        <div>
          <p>{props.property ? props.property.name : 'Checkin Form'}</p>
          <p id="property-address">{props.property ? props.property.address : ''}</p>
        </div>
      }
      onCancel={cancelModal}
      footer={
        props.hideCancel
          ? null
          : [
              <Button key="back" onClick={cancelModal} className="cancel-button">
                {<IntlMessages id="propertyPage.modal.cancel" />}
              </Button>,
            ]
      }
    >
      {/* The embedded induction takes over the whole body once the check-in form
          is done: one thing on screen at a time, on a phone at a gate. */}
      {inductionStep && !showFinalScreen && (
        <div className="form-body" style={{ padding: 20 }}>
          {/* Said plainly: they told us they had been inducted and we could not
              find it. Without this the form just reappears, which reads as the
              answer having been ignored. */}
          {inductedNotFound && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="We couldn't find a completed induction for you"
              description="It may be under a different email or phone number, or it may not have been finished. Please complete it again now."
            />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Thanks. Before you go on site, please complete your induction."
            description={
              inductionStep.mode === "chain_required"
                ? "Your check-in is recorded once this is finished."
                : "You can do this later if you need to, but it's quicker now."
            }
          />
          <SchemaFormRenderer
            form={inductionStep.induction_form}
            loading={inductionBusy}
            // From the embedded-induction payload, falling back to the
            // property's list — the induction asks about the same places the
            // check-in does.
            areas={inductionStep.areas || props.areas || (props.property && props.property.areas)}
            onSubmit={
              inductionStep.mode === "chain_required"
                ? submitCheckinWithInduction
                : submitOptionalInduction
            }
            {...(inductionStep.token
              ? {
                  isPublic: true,
                  formToken: inductionStep.token,
                  // Same shape PublicInduction uses: a FormData gated by the
                  // induction token, because the check-in upload endpoint only
                  // accepts a primary check-in form's token.
                  uploadFile: async (file) => {
                    assertPublicUploadSize(file);
                    const fd = new FormData();
                    fd.append("File", file);
                    fd.append("token", inductionStep.token);
                    fd.append("type", "induction_upload");
                    const up = await visitorInductionService.uploadPublicFile(fd);
                    return up && up.success && up.data && up.data.file ? up.data.file : null;
                  },
                }
              : formToken
              ? {
                  // Required mode: no induction exists until it is submitted, so
                  // there is no induction token. Gate the upload by the check-in
                  // form token instead — without this the renderer fell back to
                  // the authed /attachments endpoint, which 401s for a visitor.
                  uploadFile: async (file) => {
                    assertPublicUploadSize(file);
                    const fd = new FormData();
                    fd.append("File", file);
                    fd.append("visitor_form_token", formToken);
                    fd.append("type", "induction_upload");
                    const up = await visitorFormService.uploadFile(fd);
                    return up && up.file && up.file.path
                      ? {
                          name: up.file.name || file.name,
                          path: up.file.path,
                          url: up.file.url,
                          storage: up.file.storage,
                          type: up.file.type || file.type,
                        }
                      : null;
                  },
                }
              : {})}
          />
          {/* Optional only. Required mode offers no way past, because there is
              no check-in to keep if they leave. */}
          {inductionStep.mode === "chain_optional" && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button type="link" disabled={inductionBusy} onClick={finishOptional}>
                Skip for now
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Schema forms are self-contained — the schema IS the whole form, so the
          hardcoded Visitor Information block is only shown for legacy check-in forms. */}
      {!showFinalScreen && !inductionStep && !(dataForm && dataForm.schema_json) && renderVisitorInfo()}
      {/* Schema engine (Phase 5): schema_json present -> new renderer; else the
          untouched survey/type paths below (all guarded by !schema_json). */}
      {dataForm && dataForm.schema_json && !showFinalScreen && !inductionStep && (
        <SchemaFormRenderer
          form={renderedForm}
          formToken={formToken}
          hosts={props.property && props.property.hosts}
          // The property's Areas, for any "Property Area / Location" field.
          // Served alongside the property on both public form payloads.
          areas={props.areas || (props.property && props.property.areas)}
          loading={loading}
          onSubmit={handleSchemaSubmit}
        />
      )}
      {dataForm && !dataForm.schema_json && dataForm.type == "self_create" && !showFinalScreen
        && surveyContent && Array.isArray(surveyContent.pages) && surveyContent.pages.length > 0 && (
        <Survey.Survey json={surveyContent} onComplete={submitSurveyData} />
      )}
      {/* Self_create survey with no questions — show a header + note instead of a blank page. */}
      {dataForm && !dataForm.schema_json && dataForm.type == "self_create" && !showFinalScreen
        && surveyContent && Array.isArray(surveyContent.pages) && surveyContent.pages.length === 0 && (
        <Card style={{ margin: 20 }}>
          <h2>{dataForm.survey_title || dataForm.name}</h2>
          {dataForm.survey_description && <p>{dataForm.survey_description}</p>}
          <p style={{ color: "#888" }}>This form has no questions to display.</p>
        </Card>
      )}
      {showFinalScreen && (props.simpleFinal ? (
        <div className="property-checkin-thankyou" style={{ textAlign: "center", padding: "48px 24px" }}>
          <h3>Thank you</h3>
        </div>
      ) : (
        <FinalScreen cancelModal={cancelModal}/>
      ))}
      {dataForm && !dataForm.schema_json && dataForm.type == "pdf_upload" && !showFinalScreen && (
        <Row>
          <Col xs={{ span: 24 }} md={{ span: 12 }}>
            <iframe
              src={ dataForm.files && dataForm.files.length ?
                process.env.REACT_APP_API_BASE_URL +
                dataForm.files[dataForm.files.length - 1].path : ''
              }
            />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 11, offset: 1 }}>
            <Form
              layout="vertical"
              scrollToFirstError
              className="form-body"
              form={pdfForm}
            >
              <Form.Item
                name={["form", "is_agree"]}
                className="isoInputWrapper"
                valuePropName="checked"
              >
                <Checkbox>
                  {<IntlMessages id="formPage.modal.agreeConfirm" />}
                </Checkbox>
              </Form.Item>
              <h3>{<IntlMessages id="formPage.modal.signature" />}</h3>
              <div className="signature-area">
                <SignatureCanvas ref={padRef} />
                <button
                  onClick={handleClear}
                  className="signature-clear-button"
                >
                  Clear
                </button>
              </div>
              <div className="isoInputWrapper">
                <Button
                  key="submit"
                  className="btn-success"
                  type="primary"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  {<IntlMessages id="propertyPage.modal.save" />}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      )}
      {dataForm && !dataForm.schema_json && dataForm.type == "system_forms" && !showFinalScreen && (
        <GlobalForm
          formData={dataForm}
          setShowFinalScreen={setShowFinalScreen}
          visitorForm={visitorForm}
          visitorFormToken={formToken}
        />
      )}
      {dataForm && !dataForm.schema_json && dataForm.type == "bio_form" && !showFinalScreen && (
        <BioForm
          formData={dataForm}
          setShowFinalScreen={setShowFinalScreen}
          isAbleToSubmit={true}
          visitorForm={visitorForm}
          visitorFormToken={formToken}
        />
      )}
      {dataForm && !dataForm.schema_json && dataForm.type == "checkin_form" && !showFinalScreen && (
        <CheckinForm
          formData={dataForm}
          setShowFinalScreen={setShowFinalScreen}
          isAbleToSubmit={true}
          visitorForm={visitorForm}
          visitorFormToken={formToken}
        />
      )}
      {dataForm && !dataForm.schema_json && dataForm.type == "hazard_form" && !showFinalScreen && (
        <HazardForm
          formData={dataForm}
          setShowFinalScreen={setShowFinalScreen}
          isAbleToSubmit={true}
          visitorForm={visitorForm}
          visitorFormToken={formToken}
          property={props.property}
        />
      )}
    </VisitorFormModalWrapper>
  );
};

export default VisitorFormModal;
